import { Box, Button, Dialog, Stack } from '@mui/material';
import TeamDto from 'common/dto-types/team-dto';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { SyncFdcpForm } from 'components/sync-fdcp-form';
import DeleteButton from 'pages/calendar/delete-button';
import { useMemo, useState } from 'react';
import { Row } from 'react-table';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import { PopupTransition } from '../../components/@extended/Transitions';
import { dispatch, useSelector } from '../../store';
import { openSnackbar } from '../../store/reducers/snackbar';
import { closeFetchFdcpForm, openCreateForm, openFetchFdcpForm } from '../../store/reducers/team';
import { createTeam, deleteTeam, fetchTeamList, synchronizeFdcpTeams } from './api';
import TeamTableColumnConfig, {
    DefaultVisibleTeamColumns,
    SearchableTeamTableColumnConfig,
} from './team-column-config';
import TeamForm from './team-form';

const TeamListPage = () => {
    const [teamList, setTeamList] = useState<Array<TeamDto>>([]);
    const defaultVisibleColumnConfig = useMemo(() => DefaultVisibleTeamColumns, []);

    const defaultColumnConfig = useMemo(
        () => [
            ...TeamTableColumnConfig,
            {
                Header: 'Actions',
                Cell: (row: { row: Row }) => (
                    <DeleteButton
                        deleteCallback={() => {
                            const team = row.row.original as any;
                            return deleteTeam(team.id);
                        }}
                        refreshCallback={async () =>
                            await fetchData({
                                pageSize: 1000,
                                pageIndex: 0,
                                filters: [],
                                searchTerm: null,
                            })
                        }
                    ></DeleteButton>
                ),
            },
        ],
        [],
    );

    const isFormOpen = useSelector((state) => state.team.isFormOpen);

    const isFetchFdcpFormOpen = useSelector((state) => state.team.isFetchFdcpFormOpen);

    const fetchData: BaseTableFetchFuncProps = async ({ pageSize, pageIndex, filters, searchTerm }) => {
        const data = await fetchTeamList({
            pageIndex,
            pageSize,
            andSpecifications: mapFiltersToSpecification(filters),
            orSpecifications:
                searchTerm && searchTerm.length > 0
                    ? mapSearchParamToSpecification(searchTerm, SearchableTeamTableColumnConfig)
                    : undefined,
        });

        if (data) {
            setTeamList(data.elements);
        }
    };

    const createTeamHandler = async (dto: TeamDto) => {
        const data = await createTeam(dto);

        if (data) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Team created successfully!',
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            fetchData({ pageSize: 1000, pageIndex: 0, filters: [], searchTerm: null });
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Failed to create Team',
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
        }
    };

    const handleTeamSubmit = async (dto: TeamDto) => {
        await createTeamHandler(dto);
    };

    const handleFetchFdcpTeamsSubmit = async (tournamentId: string) => {
        const response = await synchronizeFdcpTeams(tournamentId);
        dispatch(closeFetchFdcpForm());
        if (response?.status === 200) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Synchronize successfully!',
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );

            await fetchData({
                pageSize: 1000,
                pageIndex: 0,
                filters: [],
                searchTerm: null,
            });
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: response?.data || 'Failed to synchronize FDCP teams',
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
        }
    };

    return (
        <MainCard content={false}>
            <Stack direction="column">
                <Box sx={{ p: 2 }}>
                    <Button
                        sx={{ mr: 2 }}
                        variant="contained"
                        color="primary"
                        onClick={() => dispatch(openCreateForm())}
                    >
                        Add Team
                    </Button>
                    <Button variant="contained" color="primary" onClick={() => dispatch(openFetchFdcpForm())}>
                        Synchronize all Teams from FDCP
                    </Button>
                </Box>
                <ScrollX>
                    <BaseTable
                        data={teamList}
                        fetchData={fetchData}
                        pageCount={0}
                        defaultColumnConfig={defaultColumnConfig}
                        defaultVisibleColumnConfig={defaultVisibleColumnConfig}
                    />
                </ScrollX>
            </Stack>
            {isFormOpen && (
                <Dialog
                    maxWidth="sm"
                    TransitionComponent={PopupTransition}
                    fullWidth
                    open={isFormOpen}
                    sx={{ '& .MuiDialog-paper': { p: 0 } }}
                >
                    <TeamForm onSubmit={handleTeamSubmit} />
                </Dialog>
            )}
            {isFetchFdcpFormOpen && (
                <Dialog
                    maxWidth="sm"
                    TransitionComponent={PopupTransition}
                    fullWidth
                    open={isFetchFdcpFormOpen}
                    sx={{ '& .MuiDialog-paper': { p: 0 } }}
                >
                    <SyncFdcpForm
                        label="Synchronize all Teams of a Tournament from FDCP"
                        onSubmit={handleFetchFdcpTeamsSubmit}
                        closeFdcpForm={() => dispatch(closeFetchFdcpForm())}
                    />
                </Dialog>
            )}
        </MainCard>
    );
};

export default TeamListPage;
